/**
 * ProcedurePendingQueue — Nurse's pending injections and CLIA-waived lab tasks
 * Shows all procedure orders from active encounters awaiting nurse documentation.
 * Lives in My Day (Procedures tab).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { toast } from 'sonner';
import { Syringe, FlaskConical, RefreshCw, Loader2, Check, User, Calendar } from 'lucide-react';
import ProcedureDocumentationModal from './ProcedureDocumentationModal';

const TASK_TYPE_CONFIG = {
  INJ: { label: 'Injection', icon: Syringe, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  LAB_POCT: { label: 'In-Office Lab', icon: FlaskConical, color: 'bg-blue-50 text-blue-700 border-blue-200' },
};

export default function ProcedurePendingQueue({ onDocumented }) {
  const [tasks, setTasks]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null); // task to document

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/procedures/pending');
      setTasks(res.data.pending_procedures || []);
    } catch { toast.error('Failed to load pending procedures'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between shrink-0">
        <div>
          <h2 className="text-sm font-bold text-slate-800">Pending Procedures</h2>
          <p className="text-[10px] text-slate-400 mt-0.5">
            Injections and in-office labs ordered by the physician — click to document
          </p>
        </div>
        <button onClick={loadTasks}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-500 hover:bg-slate-50">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* Task list */}
      <div className="flex-1 overflow-auto p-4">
        {tasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-400">
            <Check className="w-10 h-10 opacity-30 mb-3" />
            <p className="text-sm font-medium">All clear — no pending procedures</p>
            <p className="text-xs mt-1">When a physician orders an injection or lab, it will appear here</p>
          </div>
        ) : (
          <div className="space-y-2">
            {tasks.map(task => {
              const cfg = TASK_TYPE_CONFIG[task.task_type] || TASK_TYPE_CONFIG.INJ;
              const Icon = cfg.icon;
              return (
                <button key={task.action_id || task.encounter_id + task.procedure_name}
                  onClick={() => setSelected(task)}
                  className={`w-full text-left rounded-xl border px-4 py-3 hover:shadow-md transition-all
                    bg-white border-slate-200 hover:border-primary group`}
                  data-testid={`procedure-task-${task.action_id}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${cfg.color} border`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 group-hover:text-primary transition-colors">
                        {task.procedure_name}
                      </p>
                      <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                        <span className="flex items-center gap-1 text-[11px] text-slate-500">
                          <User className="w-3 h-3" /> {task.patient_name}
                        </span>
                        <span className="flex items-center gap-1 text-[11px] text-slate-400">
                          <Calendar className="w-3 h-3" /> {task.date}
                        </span>
                        {task.ordered_by && (
                          <span className="text-[11px] text-slate-400">
                            Ordered by {task.ordered_by.split(' ').slice(-1)[0]}
                          </span>
                        )}
                        {task.j_code && (
                          <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">
                            {task.j_code}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 ${cfg.color} border`}>
                      {cfg.label}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Documentation modal */}
      {selected && (
        <ProcedureDocumentationModal
          encounterId={selected.encounter_id}
          task={selected}
          onClose={() => setSelected(null)}
          onDocumented={() => {
            setSelected(null);
            loadTasks();
            onDocumented?.();
          }}
        />
      )}
    </div>
  );
}
